import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, fmtError, fmtMoney, fmtNumber, fmtDate, fmtDateTime, BILLING_LABELS, ROLE_LABELS } from "@/lib/apiClient";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, CheckCircle, XCircle, FloppyDisk, ArrowUpRight, Plus, Trash, Export, Copy, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";
import PaceWidget from "@/components/PaceWidget";
import CreativesTab from "@/components/CreativesTab";
import DetailedReportTab from "@/components/DetailedReportTab";
import SlaBadge from "@/components/SlaBadge";
import EditCampaignDialog from "@/components/EditCampaignDialog";
import { PencilSimple } from "@phosphor-icons/react";
import { downloadCsv } from "@/lib/csv";

const PRICING = ["CPM", "CPC", "CPV", "session", "CPA"];

export default function CampaignDetail() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const [campaign, setCampaign] = useState(null);
    const [reports, setReports] = useState([]);
    const [activity, setActivity] = useState([]);
    const [deliveryAssignees, setDeliveryAssignees] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const [c, r, a, da] = await Promise.all([
                api.get(`/campaigns/${id}`),
                api.get(`/reports/campaigns/${id}`),
                api.get(`/activity?campaign_id=${id}`),
                api.get(`/campaigns/${id}/delivery-assignees`).catch(() => ({ data: { assignees: [] } })),
            ]);
            setCampaign(c.data);
            setReports(r.data);
            setActivity(a.data);
            setDeliveryAssignees(da.data?.assignees || []);
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]);

    if (loading) return <div className="font-mono text-xs text-slate-400">loading…</div>;
    if (!campaign) return <div className="text-sm text-slate-500">Not found.</div>;

    const role = user?.role;
    const canExecute = ["super_admin", "execution", "execution_head"].includes(role);
    const canDelivery = ["super_admin", "delivery_head"].includes(role);
    const canReport = ["super_admin", "delivery", "delivery_head", "reporting"].includes(role);
    const canAnalyze = ["super_admin", "reporting"].includes(role);
    const canSeeRevenue = ["super_admin", "accounts", "client_servicing"].includes(role);

    // Per-role tab visibility map
    const ALL_TABS = ["overview", "execution", "delivery", "creatives", "reports", "detailed_report", "analysis", "billing", "activity"];
    const TAB_ACCESS = {
        super_admin: ALL_TABS,
        client_servicing: ["overview", "creatives", "reports", "analysis", "billing", "activity"],
        execution: ["overview", "execution", "creatives", "reports", "detailed_report", "analysis", "activity"],
        execution_head: ["overview", "execution", "creatives", "reports", "detailed_report", "analysis", "activity"],
        delivery: ["overview", "delivery", "creatives", "reports", "analysis", "activity"],
        delivery_head: ["overview", "delivery", "creatives", "reports", "analysis", "activity"],
        reporting: ["overview", "creatives", "reports", "detailed_report", "analysis", "activity"],
        accounts: ["overview", "billing", "reports", "analysis", "activity"],
        client_user: ["overview", "reports", "analysis"],
    };
    const visibleTabs = (TAB_ACCESS[role] || ["overview"]).filter((t) => {
        // Detailed report tab only for reporting_only campaigns
        if (t === "detailed_report" && (campaign?.platform_type || "dsp") !== "reporting_only") return false;
        return true;
    });
    const requestedTab = searchParams.get("tab");
    const defaultTab = (requestedTab && visibleTabs.includes(requestedTab)) ? requestedTab : (visibleTabs[0] || "overview");

    return (
        <div className="space-y-6">
            <Link to="/campaigns" className="overline inline-flex items-center gap-1 text-slate-500 hover:text-slate-900">
                <ArrowLeft size={12} /> All campaigns
            </Link>

            {/* Header */}
            <div className="bg-white border border-slate-200 p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div>
                        <div className="overline mb-1">{campaign.client_org_name}</div>
                        <h1 className="font-display text-3xl md:text-4xl tracking-tighter">{campaign.name}</h1>
                        <div className="flex flex-wrap items-center gap-3 mt-3">
                            <StatusBadge status={campaign.stage} size="lg" />
                            <SlaBadge sla={campaign.sla} size="lg" showLabel />
                            {canSeeRevenue && (
                                <span className="font-mono text-xs uppercase tracking-wider text-slate-500">{BILLING_LABELS[campaign.billing_stage]}</span>
                            )}
                            <span className="font-mono text-xs uppercase tracking-wider text-slate-400">{fmtDate(campaign.start_date)} → {fmtDate(campaign.end_date)}</span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mt-6">
                    <Stat label="Volume" value={fmtNumber(campaign.volume)} />
                    <Stat label="Daily run rate" value={fmtNumber(campaign.daily_run_rate)} />
                    {canSeeRevenue ? (
                        <>
                            <Stat label="Revenue" value={fmtMoney(campaign.revenue, campaign.currency)} />
                            <Stat label="Payout" value={fmtMoney(campaign.payout, campaign.currency)} />
                        </>
                    ) : (
                        <>
                            <Stat label="Pricing model" value={campaign.pricing_model || "—"} />
                            <Stat label="Creative type" value={campaign.creative_type || "—"} />
                        </>
                    )}
                </div>

                {/* Delivery team chip strip — visible across all tabs */}
                {(deliveryAssignees.length > 0 || role === "delivery_head" || role === "super_admin") && (
                    <div className="flex items-center gap-3 mt-4 flex-wrap" data-testid="delivery-team-header-strip">
                        <span className="overline">Delivery team</span>
                        {deliveryAssignees.length === 0 ? (
                            <button
                                type="button"
                                onClick={() => {
                                    const next = new URLSearchParams(searchParams);
                                    next.set("tab", "delivery");
                                    setSearchParams(next, { replace: true });
                                }}
                                data-testid="delivery-team-header-cta"
                                className="font-mono text-[11px] uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 hover:bg-amber-100"
                            >
                                ! Assign delivery team →
                            </button>
                        ) : (
                            <>
                                {deliveryAssignees.slice(0, 5).map((u) => (
                                    <span
                                        key={u.id}
                                        data-testid={`delivery-team-chip-${u.id}`}
                                        className={`inline-flex items-center gap-1.5 border border-slate-200 px-2 py-1 ${u.id === user?.id ? "bg-slate-900 text-white border-slate-900" : "bg-white"}`}
                                    >
                                        <span className={`w-5 h-5 inline-flex items-center justify-center font-mono text-[10px] ${u.id === user?.id ? "bg-white text-slate-900" : "bg-slate-100 text-slate-700"}`}>
                                            {u.name?.[0] || "?"}
                                        </span>
                                        <span className="text-[11px] font-medium">{u.name}</span>
                                    </span>
                                ))}
                                {deliveryAssignees.length > 5 && (
                                    <span className="font-mono text-[10px] text-slate-500">+{deliveryAssignees.length - 5}</span>
                                )}
                                {(role === "delivery_head" || role === "super_admin") && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const next = new URLSearchParams(searchParams);
                                            next.set("tab", "delivery");
                                            setSearchParams(next, { replace: true });
                                        }}
                                        data-testid="delivery-team-header-edit"
                                        className="font-mono text-[10px] uppercase tracking-wider text-[var(--c-accent)] hover:underline"
                                    >
                                        Manage →
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            <Tabs defaultValue={defaultTab} className="bg-white border border-slate-200">
                <TabsList className="w-full justify-start rounded-none bg-white border-b border-slate-200 px-2 overflow-x-auto h-auto">
                    {visibleTabs.map((t) => {
                        const label = t.replace(/_/g, " ");
                        const badge = t === "delivery" && deliveryAssignees.length > 0
                            ? <span className="ml-2 font-mono text-[9px] bg-slate-200 text-slate-700 px-1.5 py-0.5 data-[state=active]:bg-white data-[state=active]:text-slate-900">{deliveryAssignees.length}</span>
                            : (t === "delivery" && (role === "delivery_head" || role === "super_admin") && deliveryAssignees.length === 0)
                                ? <span className="ml-2 font-mono text-[9px] bg-amber-100 text-amber-800 px-1.5 py-0.5">!</span>
                                : null;
                        return (
                            <TabsTrigger
                                key={t}
                                value={t}
                                data-testid={`tab-${t}`}
                                className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-none capitalize px-4 py-3 text-xs font-mono uppercase tracking-wider"
                            >{label}{badge}</TabsTrigger>
                        );
                    })}
                </TabsList>

                {visibleTabs.includes("overview") && (
                    <TabsContent value="overview" className="p-6 md:p-8">
                        <Overview campaign={campaign} role={role} onTemplated={load} />
                    </TabsContent>
                )}
                {visibleTabs.includes("execution") && (
                    <TabsContent value="execution" className="p-6 md:p-8">
                        <ExecutionPlanForm campaign={campaign} canEdit={canExecute} onSaved={load} />
                    </TabsContent>
                )}
                {visibleTabs.includes("delivery") && (
                    <TabsContent value="delivery" className="p-6 md:p-8">
                        <DeliveryPlanForm campaign={campaign} canEdit={canDelivery} role={role} onSaved={load} />
                    </TabsContent>
                )}
                {visibleTabs.includes("creatives") && (
                    <TabsContent value="creatives" className="p-6 md:p-8">
                        <CreativesTab campaignId={campaign.id} canEdit={["super_admin", "client_servicing", "execution", "execution_head", "delivery", "delivery_head"].includes(role)} />
                    </TabsContent>
                )}
                {visibleTabs.includes("reports") && (
                    <TabsContent value="reports" className="p-6 md:p-8">
                        <ReportsTab campaign={campaign} reports={reports} canSubmitDaily={canReport} onChanged={load} />
                    </TabsContent>
                )}
                {visibleTabs.includes("detailed_report") && (
                    <TabsContent value="detailed_report" className="p-6 md:p-8">
                        <DetailedReportTab campaignId={campaign.id} role={role} />
                    </TabsContent>
                )}
                {visibleTabs.includes("analysis") && (
                    <TabsContent value="analysis" className="p-6 md:p-8">
                        <AnalysisTab campaign={campaign} reports={reports.filter(r => r.kind === "analysis")} canSubmit={canAnalyze} onChanged={load} />
                    </TabsContent>
                )}
                {visibleTabs.includes("billing") && (
                    <TabsContent value="billing" className="p-6 md:p-8">
                        <BillingTab campaign={campaign} role={role} onChanged={load} />
                    </TabsContent>
                )}
                {visibleTabs.includes("activity") && (
                    <TabsContent value="activity" className="p-6 md:p-8">
                        <ActivityTimeline items={activity} campaignName={campaign.name} />
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-white p-5">
            <div className="overline">{label}</div>
            <div className="font-mono text-2xl mt-1">{value}</div>
        </div>
    );
}

function Overview({ campaign, role, onTemplated }) {
    const canTemplate = role === "super_admin" || role === "client_servicing";
    const canEdit = role === "super_admin" || role === "client_servicing";
    const canPushExec = (role === "super_admin" || role === "client_servicing") && campaign?.stage === "intake";
    const [savingTpl, setSavingTpl] = useState(false);
    const [editing, setEditing] = useState(false);
    const [pushing, setPushing] = useState(false);

    const pushToExecution = async () => {
        if (pushing) return;
        setPushing(true);
        try {
            await api.post(`/campaigns/${campaign.id}/push-to-execution`);
            toast.success("Pushed to execution — Execution Head notified");
            onTemplated?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally { setPushing(false); }
    };

    const exportDetails = () => {
        const stamp = new Date().toISOString().slice(0, 10);
        const safeName = (campaign.name || "campaign").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 60);
        downloadCsv(
            `pulse-campaign-${safeName}-${stamp}.csv`,
            [
                { key: "field", label: "Field" },
                { key: "value", label: "Value" },
            ],
            [
                ["Name", campaign.name],
                ["Client", campaign.client_org_name],
                ["Stage", campaign.stage],
                ["Pricing model", campaign.pricing_model || "—"],
                ["Creative type", campaign.creative_type || "—"],
                ["Platforms", (campaign.platforms || []).join(" | ") || "—"],
                ["Currency", campaign.currency || "USD"],
                ["Volume", campaign.volume],
                ["Daily run rate", campaign.daily_run_rate],
                ["Revenue", campaign.revenue],
                ["Payout", campaign.payout],
                ["Start date", campaign.start_date],
                ["End date", campaign.end_date],
                ["Billing stage", campaign.billing_stage],
                ["Invoice number", campaign.invoice_number || "—"],
                ["Created at", campaign.created_at],
                ["Updated at", campaign.updated_at],
                ["Targeting", campaign.targeting || ""],
                ["Notes", campaign.notes || ""],
            ].map(([field, value]) => ({ field, value })),
        );
        toast.success("Campaign details exported");
    };

    const saveAsTemplate = async () => {
        const name = window.prompt("Template name", `Template — ${campaign.name}`);
        if (!name || !name.trim()) return;
        setSavingTpl(true);
        try {
            await api.post("/campaign-templates", { name: name.trim(), campaign_id: campaign.id });
            toast.success("Saved as template");
            onTemplated?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setSavingTpl(false);
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="overline">Campaign details</div>
                <div className="flex flex-wrap gap-2">
                    {canPushExec && (
                        <Button
                            onClick={pushToExecution}
                            disabled={pushing}
                            data-testid="overview-push-to-execution"
                            className="rounded-none bg-slate-900 hover:bg-slate-800 text-white gap-2 h-8 text-xs"
                        >
                            {pushing ? "Pushing…" : "Push to Execution →"}
                        </Button>
                    )}
                    {canEdit && (
                        <Button
                            onClick={() => setEditing(true)}
                            variant="outline"
                            data-testid="overview-edit-campaign"
                            className="rounded-none border-slate-300 gap-2 h-8 text-xs"
                        >
                            <PencilSimple size={12} weight="bold" /> Edit campaign
                        </Button>
                    )}
                    <Button
                        onClick={exportDetails}
                        variant="outline"
                        data-testid="overview-export-details"
                        className="rounded-none border-slate-300 gap-2 h-8 text-xs"
                    >
                        <Export size={12} weight="bold" /> Export details
                    </Button>
                    {canTemplate && (
                        <Button
                            onClick={saveAsTemplate}
                            disabled={savingTpl}
                            variant="outline"
                            data-testid="overview-save-template"
                            className="rounded-none border-slate-300 gap-2 h-8 text-xs"
                        >
                            <Copy size={12} weight="bold" /> {savingTpl ? "Saving…" : "Save as template"}
                        </Button>
                    )}
                </div>
            </div>
            <PaceWidget campaignId={campaign.id} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                <Info label="Pricing model" value={campaign.pricing_model || "—"} />
                <Info label="Creative type" value={campaign.creative_type || "—"} />
                <Info label="Platforms" value={(campaign.platforms || []).join(", ") || "—"} />
                <Info label="Created" value={fmtDateTime(campaign.created_at)} />
                <Info label="Targeting" value={campaign.targeting || "—"} full />
                <Info label="Notes" value={campaign.notes || "—"} full />
            </div>
            {canEdit && (
                <EditCampaignDialog
                    campaign={campaign}
                    open={editing}
                    onOpenChange={setEditing}
                    onSaved={() => { setEditing(false); onTemplated?.(); }}
                />
            )}
        </div>
    );
}

function Info({ label, value, full = false }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <div className="overline mb-1">{label}</div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">{value}</div>
        </div>
    );
}

function ExecutionPlanForm({ campaign, canEdit, onSaved }) {
    const plan = campaign.execution_plan || {};
    const [form, setForm] = useState({
        objectives: plan.objectives || "",
        target_audience: plan.target_audience || "",
        kpis: plan.kpis || "",
        timeline: plan.timeline || "",
        risks: plan.risks || "",
        notes: plan.notes || "",
        checklist: plan.checklist?.length ? plan.checklist : [
            { label: "Brief confirmed", done: false },
            { label: "Creative assets received", done: false },
            { label: "Targeting validated", done: false },
            { label: "Tracking setup", done: false },
        ],
    });
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/campaigns/${campaign.id}/execution-plan`, form);
            toast.success("Execution plan saved");
            onSaved?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    const pushToDelivery = async () => {
        setBusy(true);
        try {
            // Save the plan first to ensure latest data is persisted
            await api.put(`/campaigns/${campaign.id}/execution-plan`, form);
            await api.post(`/campaigns/${campaign.id}/push-to-delivery`);
            toast.success("Pushed to Delivery Head — notification sent");
            onSaved?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    const toggleCheck = (i) => {
        const next = [...form.checklist];
        next[i] = { ...next[i], done: !next[i].done };
        setForm({ ...form, checklist: next });
    };

    return (
        <div className="space-y-6">
            <div>
                <div className="overline mb-1">Execution plan</div>
                <h3 className="font-display text-xl">Brief → Execution sheet</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Lab label="Objectives"><Textarea disabled={!canEdit} data-testid="exec-objectives" rows={3} value={form.objectives} onChange={(e) => setForm({ ...form, objectives: e.target.value })} className="rounded-none border-slate-300" /></Lab>
                <Lab label="Target audience"><Textarea disabled={!canEdit} data-testid="exec-audience" rows={3} value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} className="rounded-none border-slate-300" /></Lab>
                <Lab label="KPIs"><Textarea disabled={!canEdit} data-testid="exec-kpis" rows={2} value={form.kpis} onChange={(e) => setForm({ ...form, kpis: e.target.value })} className="rounded-none border-slate-300" /></Lab>
                <Lab label="Timeline"><Textarea disabled={!canEdit} data-testid="exec-timeline" rows={2} value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} className="rounded-none border-slate-300" /></Lab>
                <Lab label="Risks" full><Textarea disabled={!canEdit} data-testid="exec-risks" rows={2} value={form.risks} onChange={(e) => setForm({ ...form, risks: e.target.value })} className="rounded-none border-slate-300" /></Lab>
                <Lab label="Notes" full><Textarea disabled={!canEdit} data-testid="exec-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none border-slate-300" /></Lab>
            </div>

            <div>
                <div className="overline mb-2">Pre-launch checklist</div>
                <div className="border border-slate-200 divide-y divide-slate-200">
                    {form.checklist.map((c, i) => (
                        <label key={i} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50">
                            <input
                                type="checkbox"
                                checked={c.done}
                                disabled={!canEdit}
                                onChange={() => toggleCheck(i)}
                                className="w-4 h-4 accent-slate-900"
                                data-testid={`exec-check-${i}`}
                            />
                            <span className={c.done ? "line-through text-slate-400 text-sm" : "text-sm"}>{c.label}</span>
                        </label>
                    ))}
                </div>
            </div>

            {canEdit && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-5">
                    <div className="text-xs font-mono text-slate-500">
                        {campaign.ready_for_delivery ? (
                            <span className="inline-flex items-center gap-1.5 text-green-700">
                                <span className="w-1.5 h-1.5 bg-green-700 rounded-full" />
                                Pushed to Delivery Head
                            </span>
                        ) : (
                            <span>Save draft to keep working, or push to hand off to Delivery Head.</span>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            onClick={save}
                            disabled={busy}
                            variant="outline"
                            data-testid="exec-save"
                            className="rounded-none border-slate-300 gap-2"
                        >
                            <FloppyDisk size={14} /> {busy ? "Saving…" : "Save draft"}
                        </Button>
                        <Button
                            onClick={pushToDelivery}
                            disabled={busy}
                            data-testid="exec-push-delivery"
                            className="rounded-none bg-slate-900 hover:bg-slate-800 gap-2 text-white px-6"
                        >
                            <ArrowUpRight size={14} weight="bold" />
                            {busy ? "Working…" : (campaign.ready_for_delivery ? "Re-notify Delivery Head" : "Push to Delivery Head")}
                        </Button>
                    </div>
                </div>
            )}
            {!canEdit && <p className="text-xs font-mono text-slate-400">Read-only for your role.</p>}
        </div>
    );
}

function DeliveryPlanForm({ campaign, canEdit, role, onSaved }) {
    const plan = campaign.delivery_plan || {};
    const [form, setForm] = useState({
        strategy: plan.strategy || "",
        launch_date: plan.launch_date || "",
        status: plan.status || "planned",
        notes: plan.notes || "",
        allocations: plan.allocations?.length ? plan.allocations : (campaign.platforms || []).map((p) => ({
            platform: p, budget: 0, volume: 0, pricing_model: campaign.pricing_model || "CPM", owner_user_id: null, notes: "",
        })),
    });
    const [busy, setBusy] = useState(false);

    const canManageAssignees = role === "delivery_head" || role === "super_admin";
    const isDeliveryUser = role === "delivery";

    // Delivery role can flip status via a dedicated PATCH endpoint without editing the plan.
    const quickSetStatus = async (newStatus) => {
        try {
            await api.patch(`/campaigns/${campaign.id}/delivery-status`, { status: newStatus });
            toast.success(`Status → ${newStatus}`);
            setForm((f) => ({ ...f, status: newStatus }));
            onSaved?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        }
    };

    const updateAlloc = (i, patch) => {
        const next = [...form.allocations];
        next[i] = { ...next[i], ...patch };
        setForm({ ...form, allocations: next });
    };

    const addAlloc = () => setForm({ ...form, allocations: [...form.allocations, { platform: "Other", budget: 0, volume: 0, pricing_model: "CPM", owner_user_id: null, notes: "" }] });
    const rmAlloc = (i) => setForm({ ...form, allocations: form.allocations.filter((_, idx) => idx !== i) });

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/campaigns/${campaign.id}/delivery-plan`, {
                ...form,
                allocations: form.allocations.map((a) => ({ ...a, budget: Number(a.budget) || 0, volume: Number(a.volume) || 0 })),
            });
            toast.success("Delivery plan saved");
            onSaved?.();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-6">
            <DeliveryAssigneesPanel
                campaign={campaign}
                canManage={canManageAssignees}
                isDeliveryUser={isDeliveryUser}
                onUpdated={onSaved}
            />

            <div className="border-t border-slate-200 pt-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="overline mb-1">Delivery plan</div>
                        <h3 className="font-display text-xl leading-tight">Channel allocation &amp; launch</h3>
                        {isDeliveryUser && (
                            <p className="text-xs font-mono text-slate-500 mt-2">
                                Read-only · plan is saved by the Delivery Head. You can update the status below.
                            </p>
                        )}
                    </div>
                    {isDeliveryUser && (
                        <div className="min-w-[220px]" data-testid="delivery-quick-status">
                            <div className="overline mb-1.5">Update status</div>
                            <Select value={form.status} onValueChange={quickSetStatus}>
                                <SelectTrigger data-testid="delivery-quick-status-trigger" className="rounded-none h-10 border-slate-300">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {["launched", "paused", "completed"].map((s) => (
                                        <SelectItem key={s} value={s} data-testid={`delivery-quick-status-option-${s}`}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>
            </div>

            <div className="overflow-x-auto border border-slate-200">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="text-left px-3 py-2 overline">Platform</th>
                            <th className="text-left px-3 py-2 overline">Pricing</th>
                            <th className="text-right px-3 py-2 overline">Budget (%)</th>
                            <th className="text-right px-3 py-2 overline">Volume</th>
                            <th className="text-left px-3 py-2 overline">Notes</th>
                            {canEdit && <th />}
                        </tr>
                    </thead>
                    <tbody>
                        {form.allocations.map((a, i) => (
                            <tr key={i} className="border-t border-slate-200" data-testid={`alloc-row-${i}`}>
                                <td className="px-3 py-2"><Input disabled={!canEdit} value={a.platform} onChange={(e) => updateAlloc(i, { platform: e.target.value })} className="rounded-none border-slate-300 h-9" /></td>
                                <td className="px-3 py-2">
                                    <Select disabled={!canEdit} value={a.pricing_model} onValueChange={(v) => updateAlloc(i, { pricing_model: v })}>
                                        <SelectTrigger className="rounded-none h-9 border-slate-300 w-28"><SelectValue /></SelectTrigger>
                                        <SelectContent>{PRICING.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                    </Select>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex justify-end">
                                        <Input disabled={!canEdit} type="number" value={a.budget} onChange={(e) => updateAlloc(i, { budget: e.target.value })} className="rounded-none border-slate-300 h-9 text-right font-mono w-32" />
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex justify-end">
                                        <Input disabled={!canEdit} type="number" value={a.volume} onChange={(e) => updateAlloc(i, { volume: e.target.value })} className="rounded-none border-slate-300 h-9 text-right font-mono w-32" />
                                    </div>
                                </td>
                                <td className="px-3 py-2"><Input disabled={!canEdit} value={a.notes || ""} onChange={(e) => updateAlloc(i, { notes: e.target.value })} className="rounded-none border-slate-300 h-9" /></td>
                                {canEdit && (
                                    <td className="px-3 py-2">
                                        <button onClick={() => rmAlloc(i)} className="text-slate-400 hover:text-red-600" data-testid={`alloc-remove-${i}`}><Trash size={16} /></button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {canEdit && (
                    <div className="border-t border-slate-200 p-2">
                        <button onClick={addAlloc} data-testid="alloc-add" className="text-xs font-mono uppercase tracking-wider text-[var(--c-accent)] hover:underline inline-flex items-center gap-1 px-2 py-1">
                            <Plus size={12} /> Add platform
                        </button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Lab label="Status">
                    <Select disabled={!canEdit} value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger data-testid="delivery-status" className="rounded-none h-10 border-slate-300"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {["planned", "ready_to_launch", "launched", "paused", "completed"].map((s) => (
                                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Lab>
                <Lab label="Launch date"><Input disabled={!canEdit} type="date" value={form.launch_date || ""} onChange={(e) => setForm({ ...form, launch_date: e.target.value })} className="rounded-none border-slate-300" /></Lab>
            </div>

            <Lab label="Strategy"><Textarea disabled={!canEdit} rows={3} value={form.strategy} onChange={(e) => setForm({ ...form, strategy: e.target.value })} className="rounded-none border-slate-300" data-testid="delivery-strategy" /></Lab>
            <Lab label="Notes"><Textarea disabled={!canEdit} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none border-slate-300" /></Lab>

            {canEdit && (
                <div className="flex justify-end">
                    <Button onClick={save} disabled={busy} data-testid="delivery-save" className="rounded-none bg-slate-900 hover:bg-slate-800 gap-2 text-white px-6">
                        <FloppyDisk size={14} /> {busy ? "Saving…" : "Save delivery plan"}
                    </Button>
                </div>
            )}
        </div>
    );
}

function exportReportsCsv(reports, kind, campaignName) {
    if (!reports.length) {
        toast.info("Nothing to export.");
        return;
    }
    const stamp = new Date().toISOString().slice(0, 10);
    const safeName = (campaignName || "campaign").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 60);
    // Collect all metric keys across reports for dynamic columns
    const metricKeys = new Set();
    for (const r of reports) {
        const m = r.data?.metrics || {};
        Object.keys(m).forEach((k) => metricKeys.add(k));
    }
    const baseCols = [
        { key: "id", label: "ID" },
        { key: "status", label: "Status" },
        { key: "created_by_name", label: "Submitted by" },
        { key: "created_at", label: "Submitted at" },
        { key: "approved_at", label: "Approved at" },
        { key: "rejection_reason", label: "Rejection reason" },
    ];
    const dataCols =
        kind === "daily"
            ? [
                  { key: "report_date", label: "Date" },
                  { key: "impressions", label: "Impressions" },
                  { key: "clicks", label: "Clicks" },
                  { key: "reach", label: "Reach" },
                  { key: "conversions", label: "Conversions" },
                  { key: "first_quartile", label: "1st quartile (25%)" },
                  { key: "second_quartile", label: "2nd quartile (50%)" },
                  { key: "third_quartile", label: "3rd quartile (75%)" },
                  { key: "completed_views", label: "Completed (100%)" },
                  { key: "notes", label: "Notes" },
              ]
            : [
                  { key: "week_start", label: "Week start" },
                  { key: "week_end", label: "Week end" },
                  { key: "platform", label: "Platform" },
                  { key: "is_custom_platform", label: "Is custom platform" },
                  { key: "total_impressions", label: "Total impressions" },
                  { key: "total_clicks", label: "Total clicks" },
                  { key: "total_spend", label: "Total spend" },
                  { key: "total_revenue", label: "Total revenue" },
                  { key: "summary", label: "Summary" },
                  { key: "notes", label: "Notes" },
              ];
    const cols = [
        ...baseCols,
        ...dataCols.map((c) => ({ ...c, value: (r) => r.data?.[c.key] ?? "" })),
        ...Array.from(metricKeys).sort().map((k) => ({
            key: `metric_${k}`,
            label: `metric:${k}`,
            value: (r) => r.data?.metrics?.[k] ?? "",
        })),
    ];
    downloadCsv(`pulse-${kind}-reports-${safeName}-${stamp}.csv`, cols, reports);
    toast.success(`${reports.length} ${kind} report${reports.length === 1 ? "" : "s"} exported`);
}

function ReportsTab({ campaign, reports, canSubmitDaily, onChanged }) {
    const daily = reports.filter((r) => r.kind === "daily");
    const { user } = useAuth();
    const [editing, setEditing] = useState(null);
    const canEditReport = (r) => {
        if (["super_admin", "delivery_head"].includes(user?.role)) return true;
        return r.created_by === user?.id;
    };
    return (
        <div className="space-y-8">
            <div>
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <div>
                        <div className="overline">Daily / EOD</div>
                        <h3 className="font-display text-xl">Daily reports</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={() => exportReportsCsv(daily, "daily", campaign.name)}
                            disabled={daily.length === 0}
                            variant="outline"
                            data-testid="export-daily-csv"
                            className="rounded-none border-slate-300 gap-2 h-8 text-xs disabled:opacity-50"
                        >
                            <Export size={12} weight="bold" /> Export CSV
                        </Button>
                        {canSubmitDaily && <DailyReportButton campaignId={campaign.id} onSubmitted={onChanged} />}
                    </div>
                </div>
                <ReportTable
                    reports={daily}
                    columns={[
                        "report_date",
                        "impressions",
                        "clicks",
                        "reach",
                        "conversions",
                        "first_quartile",
                        "second_quartile",
                        "third_quartile",
                        "completed_views",
                    ]}
                    canEditReport={canEditReport}
                    onEdit={setEditing}
                />
            </div>
            {editing && (
                <EditDailyReportModal
                    campaignId={campaign.id}
                    report={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); onChanged?.(); }}
                />
            )}
        </div>
    );
}

function AnalysisTab({ campaign, reports, canSubmit, onChanged }) {
    const exportAnalysisCsv = () => {
        if (!reports.length) {
            toast.info("Nothing to export.");
            return;
        }
        const stamp = new Date().toISOString().slice(0, 10);
        const safeName = (campaign.name || "campaign").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 60);
        downloadCsv(
            `pulse-analysis-${safeName}-${stamp}.csv`,
            [
                { key: "id", label: "ID" },
                { key: "status", label: "Status" },
                { key: "created_by_name", label: "Submitted by" },
                { key: "created_at", label: "Submitted at" },
                { key: "approved_at", label: "Approved at" },
                { key: "title", label: "Title", value: (r) => r.data?.title ?? "" },
                { key: "insights", label: "Insights", value: (r) => r.data?.insights ?? "" },
                { key: "issues", label: "Issues", value: (r) => r.data?.issues ?? "" },
                { key: "recommendations", label: "Recommendations", value: (r) => r.data?.recommendations ?? "" },
                { key: "optimization_actions", label: "Optimization actions", value: (r) => r.data?.optimization_actions ?? "" },
            ],
            reports,
        );
        toast.success(`${reports.length} analysis report${reports.length === 1 ? "" : "s"} exported`);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="overline">24-hour analysis</div>
                    <h3 className="font-display text-xl">Insights & recommendations</h3>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={exportAnalysisCsv}
                        disabled={reports.length === 0}
                        variant="outline"
                        data-testid="export-analysis-csv"
                        className="rounded-none border-slate-300 gap-2 h-8 text-xs disabled:opacity-50"
                    >
                        <Export size={12} weight="bold" /> Export CSV
                    </Button>
                    {canSubmit && <AnalysisReportButton campaignId={campaign.id} onSubmitted={onChanged} />}
                </div>
            </div>

            <div className="space-y-4">
                {reports.length === 0 && <p className="text-sm text-slate-500">No analysis reports yet.</p>}
                {reports.map((r) => (
                    <div key={r.id} className="border border-slate-200 bg-white p-5" data-testid={`analysis-report-${r.id}`}>
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-medium">{r.data?.title}</div>
                            <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border border-slate-300">{r.status}</span>
                        </div>
                        <Info label="Insights" value={r.data?.insights || "—"} full />
                        <div className="mt-3"><Info label="Recommendations" value={r.data?.recommendations || "—"} full /></div>
                        <div className="mt-3 overline text-slate-400">{fmtDateTime(r.created_at)} · {r.created_by_name}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function BillingTab({ campaign, role, onChanged }) {
    const canEdit = role === "accounts" || role === "super_admin";
    const canRaiseRequest = role === "client_servicing" || role === "super_admin";
    const [form, setForm] = useState({
        billing_stage: campaign.billing_stage || "pending",
        invoice_number: campaign.invoice_number || "",
        amount: campaign.revenue || 0,
        notes: "",
    });
    const [busy, setBusy] = useState(false);
    const [requests, setRequests] = useState([]);
    const [openReq, setOpenReq] = useState(false);

    const loadRequests = async () => {
        try {
            const { data } = await api.get(`/campaigns/${campaign.id}/invoice-requests`);
            setRequests(data);
        } catch (e) { setRequests([]); }
    };
    useEffect(() => { loadRequests(); }, [campaign.id]);

    const save = async () => {
        setBusy(true);
        try {
            await api.patch(`/billing/campaigns/${campaign.id}`, { ...form, amount: Number(form.amount) || 0 });
            toast.success("Billing updated");
            onChanged?.();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const processReq = async (req, decision, accounts_response, invoice_number) => {
        try {
            await api.post(`/invoice-requests/${req.id}/process`, {
                decision,
                accounts_response,
                invoice_number,
            });
            toast.success(`Request ${decision}`);
            loadRequests();
            onChanged?.();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    };

    return (
        <div className="space-y-8">
            <div className="space-y-5 max-w-2xl">
                <div className="flex items-end justify-between gap-3">
                    <div>
                        <div className="overline mb-1">Accounts</div>
                        <h3 className="font-display text-xl">Billing status</h3>
                    </div>
                    {canRaiseRequest && (
                        <Button
                            onClick={() => setOpenReq(true)}
                            data-testid="raise-invoice-request"
                            className="rounded-none bg-slate-900 hover:bg-slate-800 text-white gap-2"
                        >
                            <Plus size={14} weight="bold" /> Request invoice
                        </Button>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Lab label="Billing stage">
                        <Select disabled={!canEdit} value={form.billing_stage} onValueChange={(v) => setForm({ ...form, billing_stage: v })}>
                            <SelectTrigger data-testid="billing-stage-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["pending", "ready_for_invoice", "invoiced", "paid"].map((s) => <SelectItem key={s} value={s}>{BILLING_LABELS[s]}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Lab>
                    <Lab label="Invoice #">
                        <Input disabled={!canEdit} data-testid="billing-invoice" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="rounded-none border-slate-300 font-mono" />
                    </Lab>
                    <Lab label="Amount">
                        <Input disabled={!canEdit} type="number" data-testid="billing-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-none border-slate-300 font-mono" />
                    </Lab>
                    <Lab label="Notes" full>
                        <Textarea disabled={!canEdit} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none border-slate-300" rows={2} />
                    </Lab>
                </div>
                {canEdit && (
                    <div className="flex justify-end">
                        <Button onClick={save} disabled={busy} data-testid="billing-save" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white px-6">
                            {busy ? "Saving…" : "Update billing"}
                        </Button>
                    </div>
                )}
            </div>

            {/* Invoice requests */}
            <div>
                <div className="overline mb-2">Invoice requests</div>
                {requests.length === 0 ? (
                    <p className="text-sm text-slate-500">No invoice requests yet.</p>
                ) : (
                    <div className="border border-slate-200 bg-white divide-y divide-slate-100">
                        {requests.map((r) => (
                            <InvoiceRequestRow
                                key={r.id}
                                req={r}
                                canProcess={canEdit && r.status === "open"}
                                onProcess={(decision, response, inv) => processReq(r, decision, response, inv)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {openReq && (
                <RaiseInvoiceRequestModal
                    campaign={campaign}
                    onClose={() => setOpenReq(false)}
                    onCreated={() => { setOpenReq(false); loadRequests(); }}
                />
            )}
        </div>
    );
}

function InvoiceRequestRow({ req, canProcess, onProcess }) {
    const [openProcess, setOpenProcess] = useState(false);
    const [invNum, setInvNum] = useState("");
    const [resp, setResp] = useState("");
    const statusStyle = {
        open: "bg-amber-50 border-amber-300 text-amber-800",
        processed: "bg-green-50 border-green-300 text-green-700",
        rejected: "bg-red-50 border-red-300 text-red-700",
    }[req.status] || "bg-slate-50 border-slate-300 text-slate-700";
    return (
        <div className="p-4" data-testid={`invoice-req-${req.id}`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border ${statusStyle}`}>{req.status}</span>
                        <span className="overline">{req.month}</span>
                    </div>
                    <div className="text-sm">
                        <span className="font-medium">{fmtMoney(req.amount, req.currency)}</span>
                        <span className="text-slate-500"> · {fmtNumber(req.delivered_volume)} delivered</span>
                    </div>
                    <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                        raised by {req.requested_by_name} · {fmtDateTime(req.created_at)}
                    </div>
                    {req.notes && <p className="text-xs text-slate-600 mt-1">{req.notes}</p>}
                    {req.accounts_response && (
                        <p className="text-xs text-slate-700 mt-1">
                            <span className="overline mr-1">accounts</span>{req.accounts_response}
                        </p>
                    )}
                    {req.invoice_number && (
                        <p className="text-xs font-mono text-slate-700 mt-1">invoice #{req.invoice_number}</p>
                    )}
                </div>
                {canProcess && (
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={() => setOpenProcess((s) => !s)}
                            variant="outline"
                            data-testid={`invoice-req-process-${req.id}`}
                            className="rounded-none border-slate-300"
                        >Process</Button>
                    </div>
                )}
            </div>
            {openProcess && canProcess && (
                <div className="mt-3 border-t border-slate-200 pt-3 space-y-2">
                    <Input value={invNum} onChange={(e) => setInvNum(e.target.value)} placeholder="Invoice #" className="rounded-none border-slate-300 font-mono" data-testid={`invoice-req-inv-${req.id}`} />
                    <Textarea value={resp} onChange={(e) => setResp(e.target.value)} placeholder="Response to client servicing (optional)" rows={2} className="rounded-none border-slate-300" />
                    <div className="flex justify-end gap-2">
                        <Button
                            onClick={() => onProcess("rejected", resp, null)}
                            variant="outline"
                            data-testid={`invoice-req-reject-${req.id}`}
                            className="rounded-none border-slate-300"
                        >Reject</Button>
                        <Button
                            onClick={() => onProcess("processed", resp, invNum)}
                            disabled={!invNum.trim()}
                            data-testid={`invoice-req-approve-${req.id}`}
                            className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
                        >Mark invoiced</Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function RaiseInvoiceRequestModal({ campaign, onClose, onCreated }) {
    const today = new Date();
    const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    const [form, setForm] = useState({
        month: defaultMonth,
        delivered_volume: campaign.volume || 0,
        amount: campaign.revenue || 0,
        notes: "",
    });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!form.month.match(/^\d{4}-\d{2}$/)) {
            toast.error("Enter month as YYYY-MM");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/campaigns/${campaign.id}/invoice-requests`, {
                month: form.month,
                delivered_volume: Number(form.delivered_volume) || 0,
                amount: Number(form.amount) || 0,
                currency: campaign.currency,
                notes: form.notes || null,
            });
            toast.success("Invoice request raised — accounts has been notified");
            onCreated?.();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="invoice-request-modal">
            <form onSubmit={submit} className="bg-white border border-slate-200 max-w-lg w-full mt-12 mb-12">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div>
                        <div className="overline">Raise</div>
                        <h3 className="font-display text-lg">Invoice request</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-2xl leading-none text-slate-500" data-testid="invoice-request-close">×</button>
                </div>
                <div className="p-6 space-y-4">
                    <Lab label="Month (YYYY-MM)">
                        <Input
                            required
                            value={form.month}
                            onChange={(e) => setForm({ ...form, month: e.target.value })}
                            placeholder="2026-05"
                            data-testid="invoice-request-month"
                            className="rounded-none border-slate-300 font-mono"
                        />
                    </Lab>
                    <Lab label="Delivered volume">
                        <Input
                            type="number"
                            value={form.delivered_volume}
                            onChange={(e) => setForm({ ...form, delivered_volume: e.target.value })}
                            data-testid="invoice-request-volume"
                            className="rounded-none border-slate-300 font-mono"
                        />
                    </Lab>
                    <Lab label={`Amount (${campaign.currency || "USD"})`}>
                        <Input
                            type="number"
                            required
                            value={form.amount}
                            onChange={(e) => setForm({ ...form, amount: e.target.value })}
                            data-testid="invoice-request-amount"
                            className="rounded-none border-slate-300 font-mono"
                        />
                    </Lab>
                    <Lab label="Notes for accounts">
                        <Textarea
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            rows={3}
                            data-testid="invoice-request-notes"
                            className="rounded-none border-slate-300"
                            placeholder="Reference any PO, contract clause, or delivery proof…"
                        />
                    </Lab>
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button type="submit" disabled={busy} data-testid="invoice-request-submit" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white">
                        {busy ? "Sending…" : "Send to accounts"}
                    </Button>
                </div>
            </form>
        </div>
    );
}

const ACTIVITY_CATEGORIES = {
    campaign: ["campaign.created", "campaign.stage_changed", "campaign.assigned", "campaign.pushed_to_delivery"],
    plan: ["execution_plan.saved", "delivery_plan.saved"],
    report: ["report.submitted"],
    approval: ["report.approved", "report.rejected"],
    billing: ["billing.updated", "invoice_request.created", "invoice_request.processed", "invoice_request.rejected"],
    creative: ["creative.added", "creative.removed"],
};

function categorize(action) {
    for (const [cat, prefixes] of Object.entries(ACTIVITY_CATEGORIES)) {
        if (prefixes.some((p) => action === p || action.startsWith(p))) return cat;
    }
    return "other";
}

function ActivityTimeline({ items, campaignName }) {
    const [filter, setFilter] = useState("all");
    const categories = ["all", ...Object.keys(ACTIVITY_CATEGORIES), "other"];
    const filtered = useMemo(() => {
        if (filter === "all") return items;
        return items.filter((a) => categorize(a.action) === filter);
    }, [items, filter]);

    const counts = useMemo(() => {
        const c = { all: items.length, other: 0 };
        for (const cat of Object.keys(ACTIVITY_CATEGORIES)) c[cat] = 0;
        for (const a of items) {
            const cat = categorize(a.action);
            c[cat] = (c[cat] || 0) + 1;
        }
        return c;
    }, [items]);

    const exportCsv = () => {
        if (filtered.length === 0) {
            toast.info("Nothing to export with the current filter.");
            return;
        }
        const stamp = new Date().toISOString().slice(0, 10);
        const safeName = (campaignName || "campaign").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 60);
        downloadCsv(
            `pulse-audit-${safeName}-${filter}-${stamp}.csv`,
            [
                { key: "created_at", label: "Timestamp" },
                { key: "actor_name", label: "Actor" },
                { key: "action", label: "Action" },
                { key: "category", label: "Category", value: (a) => categorize(a.action) },
                { key: "details", label: "Details", value: (a) => JSON.stringify(a.details || {}) },
            ],
            filtered,
        );
        toast.success(`${filtered.length} event${filtered.length === 1 ? "" : "s"} exported`);
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex gap-px bg-slate-200 border border-slate-200 flex-wrap" data-testid="activity-filter">
                    {categories.map((c) => (
                        <button
                            key={c}
                            type="button"
                            onClick={() => setFilter(c)}
                            data-testid={`activity-filter-${c}`}
                            className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider ${
                                filter === c ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
                            }`}
                        >
                            {c} <span className={filter === c ? "text-slate-300" : "text-slate-400"}>· {counts[c] || 0}</span>
                        </button>
                    ))}
                </div>
                <Button
                    onClick={exportCsv}
                    disabled={filtered.length === 0}
                    variant="outline"
                    data-testid="activity-export-csv"
                    className="rounded-none border-slate-300 gap-2 h-8 text-xs disabled:opacity-50"
                >
                    <Export size={12} weight="bold" /> Export CSV
                </Button>
            </div>

            {filtered.length === 0 ? (
                <p className="text-sm text-slate-500">No events for this filter.</p>
            ) : (
                <ol className="relative border-l border-slate-200 ml-1">
                    {filtered.map((a) => {
                        const cat = categorize(a.action);
                        return (
                            <li key={a.id} className="ml-5 mb-5" data-testid={`timeline-${a.id}`}>
                                <span className="absolute -left-[5px] w-2.5 h-2.5 bg-slate-900" />
                                <div className="text-sm flex items-center gap-2 flex-wrap">
                                    <span className="font-medium">{a.actor_name}</span>
                                    <span className="text-slate-500">—</span>
                                    <span className="font-mono text-xs">{a.action}</span>
                                    <span className="font-mono text-[9px] uppercase tracking-wider bg-slate-100 text-slate-700 px-1.5 py-0.5">{cat}</span>
                                </div>
                                <div className="overline text-slate-400 mt-0.5">{fmtDateTime(a.created_at)}</div>
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
}

function Lab({ label, children, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <Label className="overline mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}

function ReportTable({ reports, columns, canEditReport, onEdit }) {
    const [expanded, setExpanded] = useState(null);
    const showActions = typeof onEdit === "function";
    if (reports.length === 0) return <p className="text-sm text-slate-500">No reports yet.</p>;
    return (
        <div className="border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-slate-50">
                    <tr>
                        <th className="w-8"></th>
                        {columns.map((c) => (
                            <th key={c} className="text-left px-3 py-2 overline">{c.replace(/_/g, " ")}</th>
                        ))}
                        <th className="text-left px-3 py-2 overline">Submitted</th>
                        {showActions && <th className="text-right px-3 py-2 overline">Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {reports.map((r) => {
                        const isOpen = expanded === r.id;
                        const editable = showActions && canEditReport?.(r);
                        return (
                            <React.Fragment key={r.id}>
                                <tr
                                    className={`border-t border-slate-200 cursor-pointer hover:bg-slate-50 ${isOpen ? "bg-slate-50" : ""}`}
                                    onClick={() => setExpanded(isOpen ? null : r.id)}
                                    data-testid={`report-row-${r.id}`}
                                >
                                    <td className="px-2 py-2 text-slate-400 font-mono">{isOpen ? "−" : "+"}</td>
                                    {columns.map((c) => (
                                        <td key={c} className="px-3 py-2 font-mono text-xs">{r.data?.[c] ?? "—"}</td>
                                    ))}
                                    <td className="px-3 py-2 font-mono text-xs">{fmtDateTime(r.created_at)}</td>
                                    {showActions && (
                                        <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                            {editable ? (
                                                <button
                                                    type="button"
                                                    onClick={() => onEdit(r)}
                                                    data-testid={`report-edit-${r.id}`}
                                                    className="p-1.5 text-slate-500 hover:text-slate-900"
                                                    title="Edit"
                                                >
                                                    <PencilSimple size={14} />
                                                </button>
                                            ) : (
                                                <span className="text-slate-300 text-xs font-mono">—</span>
                                            )}
                                        </td>
                                    )}
                                </tr>
                                {isOpen && (
                                    <tr className="bg-slate-50" data-testid={`report-row-expanded-${r.id}`}>
                                        <td></td>
                                        <td colSpan={columns.length + (showActions ? 2 : 1)} className="px-3 pb-4 pt-1">
                                            <ReportDetail report={r} />
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function ReportDetail({ report }) {
    const metricsObj = report.data?.metrics || {};
    const metricEntries = Object.entries(metricsObj);
    const customDefs = report.data?.custom_metric_defs || [];
    const defByKey = Object.fromEntries(customDefs.map((d) => [d.key, d]));
    return (
        <div className="bg-white border border-slate-200 p-4 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                {report.data?.platform && (
                    <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 bg-slate-900 text-white">
                        {report.data.platform}
                    </span>
                )}
                {report.data?.is_custom_platform && (
                    <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[var(--c-vermilion)] text-white">
                        custom
                    </span>
                )}
                {report.rejection_reason && (
                    <span className="text-xs text-red-700">Rejected: {report.rejection_reason}</span>
                )}
                <span className="overline text-slate-400">by {report.created_by_name}</span>
            </div>

            {metricEntries.length > 0 && (
                <div>
                    <div className="overline mb-2">Platform metrics</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                        {metricEntries.map(([k, v]) => {
                            const def = defByKey[k];
                            const label = def?.label || k.replace(/_/g, " ");
                            const unit = def?.unit || "";
                            const isPercent = def?.type === "percent" || /(_rate|ctr|cvr|frequency|roas)$/.test(k);
                            return (
                                <div key={k} className="bg-white p-2.5">
                                    <div className="overline" style={{ fontSize: 9 }}>{label}</div>
                                    <div className="font-mono text-sm mt-0.5">
                                        {unit && <span className="text-slate-400">{unit}</span>}
                                        {Number(v).toLocaleString()}
                                        {isPercent && <span className="text-slate-400 ml-0.5">%</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {report.data?.summary && (
                <div>
                    <div className="overline mb-1">Summary</div>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{report.data.summary}</p>
                </div>
            )}
            {report.data?.notes && (
                <div>
                    <div className="overline mb-1">Notes</div>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{report.data.notes}</p>
                </div>
            )}
        </div>
    );
}

function ReportFileLink({ fileId, kind }) {
    const [busy, setBusy] = useState(false);
    const download = async (e) => {
        e.stopPropagation();
        setBusy(true);
        try {
            const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${kind || "report"}-${fileId.slice(0, 6)}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Download failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            onClick={download}
            data-testid={`report-file-download-${fileId}`}
            disabled={busy}
            className="text-[10px] font-mono uppercase tracking-wider text-[var(--c-accent)] hover:underline"
        >
            {busy ? "…" : "Download"}
        </button>
    );
}

// --- Inline submit modals ---
function DailyReportButton({ campaignId, onSubmitted }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        report_date: new Date().toISOString().slice(0, 10),
        impressions: 0,
        clicks: 0,
        first_quartile: 0,
        second_quartile: 0,
        third_quartile: 0,
        completed_views: 0,
        conversions: 0,
        reach: 0,
        notes: "",
    });
    const [busy, setBusy] = useState(false);
    const num = (k) => Number(form[k]) || 0;
    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/reports/campaigns/${campaignId}/daily`, {
                report_date: form.report_date,
                impressions: num("impressions"),
                clicks: num("clicks"),
                first_quartile: num("first_quartile"),
                second_quartile: num("second_quartile"),
                third_quartile: num("third_quartile"),
                completed_views: num("completed_views"),
                conversions: num("conversions"),
                reach: num("reach"),
                notes: form.notes,
            });
            toast.success("EOD report submitted");
            setOpen(false);
            onSubmitted?.();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    if (!open) {
        return <button onClick={() => setOpen(true)} data-testid="submit-daily-button" className="bg-slate-900 text-white px-4 py-2 text-xs font-mono uppercase tracking-wider hover:bg-slate-800">+ Daily EOD</button>;
    }
    return (
        <SmallForm title="Submit Daily / EOD Report" onClose={() => setOpen(false)} onSubmit={submit} testid="daily-form" busy={busy}>
            <Grid2>
                <Lab label="Date"><Input type="date" value={form.report_date} onChange={(e) => setForm({ ...form, report_date: e.target.value })} data-testid="daily-report_date" className="rounded-none border-slate-300" /></Lab>
                <Lab label="Impressions"><Input type="number" value={form.impressions} onChange={(e) => setForm({ ...form, impressions: e.target.value })} data-testid="daily-impressions" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="Clicks"><Input type="number" value={form.clicks} onChange={(e) => setForm({ ...form, clicks: e.target.value })} data-testid="daily-clicks" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="Reach"><Input type="number" value={form.reach} onChange={(e) => setForm({ ...form, reach: e.target.value })} data-testid="daily-reach" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="Conversions"><Input type="number" value={form.conversions} onChange={(e) => setForm({ ...form, conversions: e.target.value })} data-testid="daily-conversions" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="1st quartile (25%)"><Input type="number" value={form.first_quartile} onChange={(e) => setForm({ ...form, first_quartile: e.target.value })} data-testid="daily-first_quartile" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="2nd quartile (50%)"><Input type="number" value={form.second_quartile} onChange={(e) => setForm({ ...form, second_quartile: e.target.value })} data-testid="daily-second_quartile" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="3rd quartile (75%)"><Input type="number" value={form.third_quartile} onChange={(e) => setForm({ ...form, third_quartile: e.target.value })} data-testid="daily-third_quartile" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="Completed views (100%)"><Input type="number" value={form.completed_views} onChange={(e) => setForm({ ...form, completed_views: e.target.value })} data-testid="daily-completed_views" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="Notes" full><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="daily-notes" className="rounded-none border-slate-300" /></Lab>
            </Grid2>
        </SmallForm>
    );
}

function EditDailyReportModal({ campaignId, report, onClose, onSaved }) {
    const initialData = report.data || {};
    const [form, setForm] = useState({
        report_date: initialData.report_date || new Date().toISOString().slice(0, 10),
        impressions: initialData.impressions ?? 0,
        clicks: initialData.clicks ?? 0,
        first_quartile: initialData.first_quartile ?? 0,
        second_quartile: initialData.second_quartile ?? 0,
        third_quartile: initialData.third_quartile ?? 0,
        completed_views: initialData.completed_views ?? 0,
        conversions: initialData.conversions ?? 0,
        reach: initialData.reach ?? 0,
        notes: initialData.notes || "",
    });
    const [busy, setBusy] = useState(false);
    const num = (k) => Number(form[k]) || 0;
    const submit = async () => {
        setBusy(true);
        try {
            await api.patch(`/reports/campaigns/${campaignId}/daily/${report.id}`, {
                report_date: form.report_date,
                impressions: num("impressions"),
                clicks: num("clicks"),
                first_quartile: num("first_quartile"),
                second_quartile: num("second_quartile"),
                third_quartile: num("third_quartile"),
                completed_views: num("completed_views"),
                conversions: num("conversions"),
                reach: num("reach"),
                notes: form.notes,
            });
            toast.success("Daily report updated");
            onSaved?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };
    return (
        <SmallForm title={`Edit daily report — ${report.data?.report_date || ""}`} onClose={onClose} onSubmit={submit} testid="edit-daily-form" busy={busy}>
            <Grid2>
                <Lab label="Date"><Input type="date" value={form.report_date} onChange={(e) => setForm({ ...form, report_date: e.target.value })} data-testid="edit-daily-report_date" className="rounded-none border-slate-300" /></Lab>
                <Lab label="Impressions"><Input type="number" value={form.impressions} onChange={(e) => setForm({ ...form, impressions: e.target.value })} data-testid="edit-daily-impressions" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="Clicks"><Input type="number" value={form.clicks} onChange={(e) => setForm({ ...form, clicks: e.target.value })} data-testid="edit-daily-clicks" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="Reach"><Input type="number" value={form.reach} onChange={(e) => setForm({ ...form, reach: e.target.value })} data-testid="edit-daily-reach" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="Conversions"><Input type="number" value={form.conversions} onChange={(e) => setForm({ ...form, conversions: e.target.value })} data-testid="edit-daily-conversions" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="1st quartile (25%)"><Input type="number" value={form.first_quartile} onChange={(e) => setForm({ ...form, first_quartile: e.target.value })} data-testid="edit-daily-first_quartile" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="2nd quartile (50%)"><Input type="number" value={form.second_quartile} onChange={(e) => setForm({ ...form, second_quartile: e.target.value })} data-testid="edit-daily-second_quartile" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="3rd quartile (75%)"><Input type="number" value={form.third_quartile} onChange={(e) => setForm({ ...form, third_quartile: e.target.value })} data-testid="edit-daily-third_quartile" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="Completed views (100%)"><Input type="number" value={form.completed_views} onChange={(e) => setForm({ ...form, completed_views: e.target.value })} data-testid="edit-daily-completed_views" className="rounded-none border-slate-300 font-mono" /></Lab>
                <Lab label="Notes" full><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-daily-notes" className="rounded-none border-slate-300" /></Lab>
            </Grid2>
        </SmallForm>
    );
}



function WeeklyReportButton({ campaignId, onSubmitted }) {
    const [open, setOpen] = useState(false);
    const [templates, setTemplates] = useState({});
    const [platform, setPlatform] = useState("DV360");
    const [customName, setCustomName] = useState("");
    const [customMetricDefs, setCustomMetricDefs] = useState([
        { key: "impressions", label: "Impressions", type: "number" },
        { key: "spend", label: "Spend", type: "number", unit: "$" },
    ]);
    const [metrics, setMetrics] = useState({});
    const [file, setFile] = useState(null);
    const [form, setForm] = useState({
        week_start: new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10),
        week_end: new Date().toISOString().slice(0, 10),
        summary: "", total_impressions: 0, total_clicks: 0, total_spend: 0, total_revenue: 0, notes: "",
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/platforms/templates");
                setTemplates(data);
            } catch (e) { setTemplates({}); }
        })();
    }, [open]);

    const isCustom = platform === "__custom__";
    const tpl = !isCustom ? templates[platform] : null;
    const fields = isCustom ? customMetricDefs : (tpl?.metrics || []);

    const setMetric = (key, v) => setMetrics((m) => ({ ...m, [key]: v }));

    // Auto-populate totals from metrics for known keys
    useEffect(() => {
        const imp = Number(metrics.impressions || 0);
        const clk = Number(metrics.clicks || metrics.link_clicks || 0);
        const spd = Number(metrics.spend || 0);
        setForm((f) => ({ ...f, total_impressions: imp, total_clicks: clk, total_spend: spd }));
    }, [metrics]);

    const addCustomMetric = () => {
        const next = [...customMetricDefs, { key: `custom_${customMetricDefs.length + 1}`, label: "Metric", type: "number" }];
        setCustomMetricDefs(next);
    };
    const removeCustomMetric = (i) => {
        const next = customMetricDefs.filter((_, idx) => idx !== i);
        setCustomMetricDefs(next);
    };
    const updateCustomMetricLabel = (i, label) => {
        const next = [...customMetricDefs];
        next[i] = { ...next[i], label, key: label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || `custom_${i + 1}` };
        setCustomMetricDefs(next);
    };

    const submit = async () => {
        if (isCustom && !customName.trim()) {
            toast.error("Enter a custom platform name");
            return;
        }
        setBusy(true);
        try {
            const cleanMetrics = {};
            for (const f of fields) {
                cleanMetrics[f.key] = Number(metrics[f.key] || 0);
            }
            await api.post(`/reports/campaigns/${campaignId}/weekly`, {
                week_start: form.week_start,
                week_end: form.week_end,
                platform: isCustom ? customName.trim() : platform,
                is_custom_platform: isCustom,
                summary: form.summary,
                total_impressions: Number(form.total_impressions),
                total_clicks: Number(form.total_clicks),
                total_spend: Number(form.total_spend),
                total_revenue: Number(form.total_revenue),
                metrics: cleanMetrics,
                custom_metric_defs: isCustom ? customMetricDefs : [],
                file_id: file?.id || null,
                notes: form.notes,
            });
            toast.success("Weekly report submitted");
            setOpen(false);
            setMetrics({});
            setFile(null);
            onSubmitted?.();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    if (!open) return <button onClick={() => setOpen(true)} data-testid="submit-weekly-button" className="bg-slate-900 text-white px-4 py-2 text-xs font-mono uppercase tracking-wider hover:bg-slate-800">+ Weekly</button>;

    return (
        <SmallForm title="Submit Weekly Report" onClose={() => setOpen(false)} onSubmit={submit} testid="weekly-form" busy={busy} wide>
            {/* Platform tabs */}
            <div>
                <Label className="overline mb-2 block">Platform template</Label>
                <div className="flex flex-wrap gap-px bg-slate-200 border border-slate-200" data-testid="platform-template-picker">
                    {Object.keys(templates).map((p) => {
                        const t = templates[p];
                        const active = platform === p;
                        return (
                            <button
                                key={p}
                                type="button"
                                onClick={() => { setPlatform(p); setMetrics({}); }}
                                data-testid={`platform-template-${p}`}
                                className={`flex-1 min-w-[140px] text-left px-3 py-2.5 bg-white transition-colors ${active ? "bg-slate-900 text-white" : "hover:bg-slate-50"}`}
                                style={active ? { backgroundColor: "#111", color: "#fff" } : {}}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2" style={{ backgroundColor: t.color || "#0033A0" }} />
                                    <span className="text-xs font-medium">{p}</span>
                                </div>
                                <div className={`text-[10px] font-mono mt-0.5 ${active ? "text-slate-300" : "text-slate-500"}`}>
                                    {t.metrics?.length || 0} fields
                                </div>
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        onClick={() => setPlatform("__custom__")}
                        data-testid="platform-template-custom"
                        className={`flex-1 min-w-[140px] text-left px-3 py-2.5 ${isCustom ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}
                    >
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-[var(--c-vermilion)]" />
                            <span className="text-xs font-medium">+ Custom platform</span>
                        </div>
                        <div className={`text-[10px] font-mono mt-0.5 ${isCustom ? "text-slate-300" : "text-slate-500"}`}>
                            define your own
                        </div>
                    </button>
                </div>
            </div>

            {isCustom && (
                <div className="space-y-3 border-l-2 border-[var(--c-vermilion)] pl-4" data-testid="custom-platform-config">
                    <Lab label="Platform name">
                        <Input
                            data-testid="custom-platform-name"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            placeholder="e.g. Reddit Ads, Spotify, Snap…"
                            className="rounded-none border-slate-300"
                        />
                    </Lab>
                    <div>
                        <Label className="overline mb-1.5 block">Custom metric fields</Label>
                        <div className="border border-slate-200 divide-y divide-slate-100">
                            {customMetricDefs.map((m, i) => (
                                <div key={i} className="flex items-center gap-2 px-2 py-1.5" data-testid={`custom-metric-def-${i}`}>
                                    <Input
                                        value={m.label}
                                        onChange={(e) => updateCustomMetricLabel(i, e.target.value)}
                                        className="rounded-none border-slate-300 h-8 flex-1"
                                        placeholder="metric name"
                                    />
                                    <span className="font-mono text-[10px] text-slate-400 w-24 truncate">{m.key}</span>
                                    <button type="button" onClick={() => removeCustomMetric(i)} className="text-slate-400 hover:text-red-600 p-1">
                                        <Trash size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={addCustomMetric}
                            data-testid="custom-metric-add"
                            className="text-xs font-mono uppercase tracking-wider text-[var(--c-accent)] hover:underline inline-flex items-center gap-1 mt-2"
                        >
                            <Plus size={12} /> Add metric field
                        </button>
                    </div>
                </div>
            )}

            <Grid2>
                <Lab label="Week start"><Input type="date" value={form.week_start} onChange={(e) => setForm({ ...form, week_start: e.target.value })} className="rounded-none border-slate-300" /></Lab>
                <Lab label="Week end"><Input type="date" value={form.week_end} onChange={(e) => setForm({ ...form, week_end: e.target.value })} className="rounded-none border-slate-300" /></Lab>
            </Grid2>

            {/* Platform-specific metric fields */}
            <div>
                <Label className="overline mb-2 block">
                    {isCustom ? "Custom metrics" : `${platform} metrics`}
                </Label>
                {fields.length === 0 ? (
                    <p className="text-xs font-mono text-slate-400">No metrics defined yet.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="platform-metric-fields">
                        {fields.map((f) => (
                            <div key={f.key}>
                                <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1 block">
                                    {f.label}
                                    {f.unit && <span className="text-slate-400 ml-1">({f.unit})</span>}
                                    {f.type === "percent" && <span className="text-slate-400 ml-1">(%)</span>}
                                </Label>
                                <Input
                                    type="number"
                                    step={f.type === "percent" ? "0.01" : "any"}
                                    value={metrics[f.key] ?? ""}
                                    onChange={(e) => setMetric(f.key, e.target.value)}
                                    className="rounded-none border-slate-300 font-mono h-9"
                                    data-testid={`metric-input-${f.key}`}
                                    placeholder="0"
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Lab label="Total revenue (commercial)" full>
                <Input type="number" value={form.total_revenue} onChange={(e) => setForm({ ...form, total_revenue: e.target.value })} className="rounded-none border-slate-300 font-mono" data-testid="weekly-total-revenue" />
            </Lab>

            <Lab label="Executive summary" full>
                <Textarea rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="rounded-none border-slate-300" data-testid="weekly-summary" />
            </Lab>

            <Lab label="Attachment (full report PDF/XLSX)" full>
                <FileUploader value={file} onChange={setFile} testid="weekly-file" />
            </Lab>
        </SmallForm>
    );
}

function AnalysisReportButton({ campaignId, onSubmitted }) {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [form, setForm] = useState({ title: "", insights: "", issues: "", recommendations: "", optimization_actions: "" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/reports/campaigns/${campaignId}/analysis`, { ...form, file_id: file?.id || null });
            toast.success("Analysis report submitted");
            setOpen(false);
            setFile(null);
            onSubmitted?.();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    if (!open) return <button onClick={() => setOpen(true)} data-testid="submit-analysis-button" className="bg-slate-900 text-white px-4 py-2 text-xs font-mono uppercase tracking-wider hover:bg-slate-800">+ Analysis</button>;
    return (
        <SmallForm title="Submit 24-hour Analysis Report" onClose={() => setOpen(false)} onSubmit={submit} testid="analysis-form" busy={busy}>
            <Lab label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none border-slate-300" /></Lab>
            <Lab label="Insights" full><Textarea rows={3} value={form.insights} onChange={(e) => setForm({ ...form, insights: e.target.value })} className="rounded-none border-slate-300" /></Lab>
            <Lab label="Issues" full><Textarea rows={2} value={form.issues} onChange={(e) => setForm({ ...form, issues: e.target.value })} className="rounded-none border-slate-300" /></Lab>
            <Lab label="Recommendations" full><Textarea rows={2} value={form.recommendations} onChange={(e) => setForm({ ...form, recommendations: e.target.value })} className="rounded-none border-slate-300" /></Lab>
            <Lab label="Optimization actions" full><Textarea rows={2} value={form.optimization_actions} onChange={(e) => setForm({ ...form, optimization_actions: e.target.value })} className="rounded-none border-slate-300" /></Lab>
            <Lab label="Attachment (analysis deck)" full>
                <FileUploader value={file} onChange={setFile} testid="analysis-file" />
            </Lab>
        </SmallForm>
    );
}

function Grid2({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>; }

function SmallForm({ title, children, onClose, onSubmit, testid, busy = false, wide = false }) {
    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid={testid}>
            <div className={`bg-white border border-slate-200 w-full mt-12 mb-12 ${wide ? "max-w-3xl" : "max-w-2xl"}`}>
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline">Submit</div>
                        <h3 className="font-display text-lg">{title}</h3>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-2xl leading-none" data-testid="form-close">×</button>
                </div>
                <div className="p-6 space-y-5">{children}</div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button onClick={onSubmit} disabled={busy} data-testid="form-submit-button" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white px-6">
                        {busy ? "Submitting…" : "Submit"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function DeliveryAssigneesPanel({ campaign, canManage, isDeliveryUser, onUpdated }) {
    const { user } = useAuth();
    const [assignees, setAssignees] = useState([]);
    const [deliveryUsers, setDeliveryUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openPick, setOpenPick] = useState(false);
    const [picked, setPicked] = useState(new Set());
    const [busy, setBusy] = useState(false);

    const loadAssignees = async () => {
        try {
            const { data } = await api.get(`/campaigns/${campaign.id}/delivery-assignees`);
            setAssignees(data.assignees || []);
            setPicked(new Set((data.assignees || []).map((u) => u.id)));
        } catch (e) {
            setAssignees([]);
        }
    };

    useEffect(() => {
        (async () => {
            await loadAssignees();
            // Only delivery_head + super_admin need the full delivery-user list
            if (canManage) {
                try {
                    const { data } = await api.get("/users");
                    setDeliveryUsers((data || []).filter((u) => u.role === "delivery" && u.is_active !== false));
                } catch (e) { /* ignore */ }
            }
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign.id]);

    const togglePick = (id) => {
        setPicked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const save = async () => {
        setBusy(true);
        try {
            await api.patch(`/campaigns/${campaign.id}/delivery-assignees`, {
                user_ids: Array.from(picked),
            });
            toast.success("Delivery assignees updated");
            setOpenPick(false);
            await loadAssignees();
            onUpdated?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const isMine = isDeliveryUser && assignees.some((u) => u.id === user?.id);

    return (
        <div className="bg-white border border-slate-200" data-testid="delivery-assignees-panel">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <UsersThree size={18} className="text-slate-500" />
                    <div>
                        <div className="overline">Delivery team</div>
                        <h3 className="font-display text-lg">
                            {assignees.length === 0 ? "No one assigned yet" : `${assignees.length} assigned`}
                        </h3>
                    </div>
                </div>
                {canManage && (
                    <Button
                        onClick={() => setOpenPick((s) => !s)}
                        data-testid="delivery-assignees-toggle"
                        variant={openPick ? "outline" : "default"}
                        className={`rounded-none ${openPick ? "border-slate-300" : "bg-slate-900 hover:bg-slate-800 text-white"} gap-2 h-8 text-xs`}
                    >
                        {openPick ? "Close" : (assignees.length === 0 ? "Assign delivery team" : "Edit assignees")}
                    </Button>
                )}
                {isDeliveryUser && (
                    <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 ${isMine ? "bg-green-50 text-green-700 border border-green-200" : "bg-slate-100 text-slate-700"}`}>
                        {isMine ? "You're assigned" : "Not assigned to you"}
                    </span>
                )}
            </div>

            {loading && <div className="px-5 py-4 text-xs font-mono text-slate-400">loading…</div>}

            {!loading && assignees.length > 0 && (
                <div className="px-5 py-4 flex flex-wrap gap-2">
                    {assignees.map((u) => (
                        <div
                            key={u.id}
                            data-testid={`delivery-assignee-chip-${u.id}`}
                            className={`inline-flex items-center gap-2 border px-3 py-1.5 ${u.id === user?.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}
                        >
                            <span className={`w-6 h-6 inline-flex items-center justify-center font-mono text-xs ${u.id === user?.id ? "bg-white text-slate-900" : "bg-slate-100 text-slate-700"}`}>
                                {u.name?.[0] || "?"}
                            </span>
                            <span className="text-xs font-medium">{u.name}</span>
                            <span className={`text-[10px] font-mono ${u.id === user?.id ? "text-slate-300" : "text-slate-500"}`}>
                                {u.email}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {canManage && openPick && (
                <div className="border-t border-slate-200 p-5 space-y-3" data-testid="delivery-assignees-picker">
                    <div className="overline">Delivery role users · {deliveryUsers.length} available · {picked.size} picked</div>
                    {deliveryUsers.length === 0 ? (
                        <p className="text-sm text-slate-500">No users with role &ldquo;delivery&rdquo; in this tenant. Add one from the Team page first.</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                            {deliveryUsers.map((u) => {
                                const isSel = picked.has(u.id);
                                return (
                                    <label
                                        key={u.id}
                                        data-testid={`delivery-user-option-${u.id}`}
                                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${isSel ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSel}
                                            onChange={() => togglePick(u.id)}
                                            className="w-4 h-4 accent-slate-900"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium truncate">{u.name}</div>
                                            <div className={`text-[10px] font-mono truncate ${isSel ? "text-slate-300" : "text-slate-500"}`}>
                                                {u.email}
                                                {u.phone && <span className="ml-2">· {u.phone}</span>}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                    <div className="flex justify-end gap-2">
                        <Button
                            onClick={() => { setPicked(new Set(assignees.map((u) => u.id))); setOpenPick(false); }}
                            variant="outline"
                            className="rounded-none border-slate-300"
                        >Cancel</Button>
                        <Button
                            onClick={save}
                            disabled={busy}
                            data-testid="delivery-assignees-save"
                            className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
                        >
                            {busy ? "Saving…" : `Save ${picked.size} assignee${picked.size === 1 ? "" : "s"}`}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

